import MainLayout from "../../../components/layout/MainLayout";
import ScreenWrapper from "../../../components/layout/screenWrapper";
import React, { useState } from "react";
import "../Analytics.scss";
import "../../../components/layout/layouts.scss";
import ButtonControl from "../../../components/controls/ButtonControl";
import AnalyticsChart from "../components/AnalyticsChart";
import { DialogControl } from "../../../components/controls/DialogControl";
import { useEffect } from "react";
import _ from "lodash";
import { useAnalyticManager } from "../../../hooks/useManagers";
import { orderCharts } from "../../../utilities/AnalyticsUtils";
import ChartConfigurationDialog from "../components/ChartConfigurationDialog";
import SystemTypes from "../../../SystemTypes";

const Analytics = () => {
  const analyticMgr = useAnalyticManager();
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);

  //Layout modifications
  const [editingLayout, setEditingLayout] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [ungriddedCharts, setUngriddedCharts] = useState([]);
  const [charts, setCharts] = useState(null);
  const [styles, setStyles] = useState(null);
  const [screenHeight, setScreenHeight] = useState(0);
  const [originalCharts, setOriginalCharts] = useState(null);
  const [originalGridData, setOriginalGridData] = useState([]);
  const [originalStyles, setOriginalStyles] = useState(null);

  //Chart Modifications
  const [chartConfig, setChartConfig] = useState(null);
  const [openAddChartDialog, setOpenAddChartDialog] = useState(false);
  const [orgs, setOrgs] = useState(null);
  const [districts, setDistricts] = useState(null);
  const [schools, setSchools] = useState(null);
  const [schoolYears, setSchoolYears] = useState(null);
  const [clusters, setClusters] = useState(null);
  const [chartToDelete, setChartToDelete] = useState(null);
  const [previewChartData, setPreviewChartData] = useState(null);
  const [previewChartKeys, setPreviewChartKeys] = useState(null);
  const [analyticConfig, setAnalyticConfig] = useState(null);

  useEffect(() => {
    if (analyticMgr && analyticMgr.AppUserState) {
      analyticMgr.grabGroup("Org").then((r) => {
        setOrgs(r);
      });
      let tmpSchoolYears = [];
      Object.keys(analyticMgr.AppUserState.schoolYearMap)
        .slice(-3)
        .forEach((schoolYear) => {
          tmpSchoolYears.push({
            Text:
              analyticMgr.AppUserState.schoolYearMap[schoolYear]?.Title ??
              schoolYear,
            Value: schoolYear,
          });
        });

      if (!chartConfig) {
        setChartConfig(analyticMgr.DefaultItem);
      }

      if (analyticMgr.DefaultItem.ClientIds) {
        updateDistricts(analyticMgr.DefaultItem.ClientIds);
      }

      convertDataToCharts();
      setSchoolYears(tmpSchoolYears);
    }
  }, [analyticMgr]);

  useEffect(() => {
    if (ungriddedCharts.length > 0) {
      const grid = convertChartsToGrid(ungriddedCharts, false, true);
      setGridData(grid);
      setUngriddedCharts([]);
      updateChartPositions(grid);
    }
  }, [ungriddedCharts]);

  const convertDataToCharts = async (defaultConfig) => {
    if (!charts) {
      if (
        analyticMgr.AppUserState.userProfile.AnalyticsConfig ||
        analyticMgr.AppUserState.DefaultAnalyticsLayout ||
        defaultConfig
      ) {
        setLoading(true);
        //Global state does not update so we use a state to store our newly made changes.
        const chartList = _.map(
          JSON.parse(
            defaultConfig ??
              analyticMgr.AppUserState.userProfile.AnalyticsConfig ??
              analyticMgr.AppUserState.DefaultAnalyticsLayout
          ),
          (x) => {
            return analyticMgr.retrieveChartData(x).then((r) => {
              if (typeof r === "object") {
                return r;
              } else {
                return { ...x, failToLoad: r };
              }
            });
          }
        );

        const result = await Promise.all(chartList);
        const grid = convertChartsToGrid(result);
        setGridData(grid);
        setCharts(updateChartPositions(grid, result));
        setLoading(false);
      }
    }
  };

  const convertChartsToGrid = (updatedCharts, reorganize) => {
    const grid =
      gridData.length > 0 && !reorganize
        ? _.cloneDeep(gridData)
        : [[false, false, false]];

    let currentRow = 0;
    _.forEach(updatedCharts ?? charts, (x, i) => {
      //Locates row with 2 empty cells.
      const rowWith2Spaces = _.findIndex(grid, (row) => {
        let count = 0;
        for (const cell of row) {
          if (!cell) {
            count += 1;
          }
        }
        if (count >= 2) {
          return true;
        }
      });
      const rowWithSpace = _.findIndex(grid, (row) => row?.includes(false));
      const emptyCellIndex = _.findIndex(grid[rowWithSpace], (col) => !col);
      if (rowWith2Spaces === -1) {
        //changed current row to gridLength,
        if (x.Size === 3) {
          grid[grid.length] = [x.Id, x.Id, false];
          grid[grid.length] = [x.Id, x.Id, false];
          currentRow += 2;
        } else if (x.Size === 2) {
          grid[grid.length] = [x.Id, x.Id, false];
          currentRow += 1;
        } else {
          if (rowWithSpace === -1) {
            grid[grid.length] = [x.Id, false, false];
            currentRow += 1;
          } else {
            grid[rowWithSpace][emptyCellIndex] = x.Id;
          }
        }
      } else {
        const twoEmptyCellIndex = _.findIndex(
          grid[rowWith2Spaces],
          (col) => !col
        );
        if (x.Size === 3) {
          if (twoEmptyCellIndex === 0) {
            grid[rowWith2Spaces][0] = x.Id;
            grid[rowWith2Spaces][1] = x.Id;
            grid[rowWith2Spaces + 1] = [
              x.Id,
              x.Id,
              grid[rowWith2Spaces + 1]
                ? (grid[rowWith2Spaces + 1][2] ?? false)
                : false,
            ];
          } else {
            grid[rowWith2Spaces][1] = x.Id;
            grid[rowWith2Spaces][2] = x.Id;
            grid[rowWith2Spaces + 1] = [
              grid[rowWith2Spaces + 1]
                ? (grid[rowWith2Spaces + 1][0] ?? false)
                : false,
              x.Id,
              x.Id,
            ];
          }
          currentRow += 1;
        } else if (x.Size === 2) {
          if (twoEmptyCellIndex === 0) {
            grid[rowWith2Spaces] = [
              x.Id,
              x.Id,
              grid[rowWith2Spaces][2] ?? false,
            ];
          } else {
            grid[rowWith2Spaces] = [
              grid[rowWith2Spaces][0] ?? false,
              x.Id,
              x.Id,
            ];
          }
        } else {
          grid[rowWithSpace][emptyCellIndex] = x.Id;
        }
      }
    });
    _.remove(grid, (x) => x.every((value) => value === false));
    return grid;
  };

  const updateChartPositions = (grid, chartsData, updateOriginal) => {
    const itemPositions = [];
    const chartStyles = [];
    let calculateScreenHeight = 0;
    for (let row = 0; row < grid.length; row++) {
      for (let col = 0; col < 3; col++) {
        const id = grid[row][col];
        const alreadyExist = _.find(itemPositions, (x) => x.Id === id);
        //I'm using false as a placeholder to see if the position is filled already. So Id must be present to push its position.
        if (!alreadyExist && id) {
          const chart = _.find(chartsData ?? charts, (x) => x.Id === id);
          itemPositions.push({
            ...chart,
            position: {
              position: "absolute",
              width: chart.Size !== 1 ? "788px" : "376px",
              height: chart.Size === 3 ? "636px" : "300px",
              top: 300 * row + 36 * row,
              left: 376 * col + 36 * col,
            },
          });
          if (col === 0) {
            calculateScreenHeight += chart.Size === 3 ? 636 : 300;
            calculateScreenHeight += 36;
          }
          chartStyles.push({
            Id: chart.Id,
            style: {
              position: "absolute",
              width: chart.Size !== 1 ? "788px" : "376px",
              height: chart.Size === 3 ? "636px" : "300px",
              top: 300 * row + 36 * row,
              left: 376 * col + 36 * col,
            },
          });
        }
      }
    }

    setScreenHeight(calculateScreenHeight);
    setStyles(chartStyles);
    if (updateOriginal) {
      setOriginalStyles(chartStyles);
    }

    return itemPositions;
  };

  const updateGrid = (rowIndex, colIndex, chart, isEnd) => {
    if (rowIndex >= gridData.length || rowIndex === -1) {
      return;
    }
    const grid = _.cloneDeep(gridData);
    let chartsPosToUpdate = [];
    if ((chart.Size == 3 || chart.Size == 2) && colIndex == 2) {
      colIndex = 1;
    }
    const checkForCollision = () => {
      let rowToCheck = chart.Size === 3 ? rowIndex + 1 : rowIndex;
      let colTocheck = chart.Size === 1 ? colIndex : colIndex + 1;
      if (rowToCheck < gridData.length) {
        for (let row = rowIndex; row <= rowToCheck; row++) {
          for (let col = colIndex; col <= colTocheck; col++) {
            const foundChart = _.find(
              charts,
              (x) => x.Id === gridData[row][col]
            );
            if (foundChart?.Size > chart.Size) {
              chartsPosToUpdate = [];
              break;
            } else if (
              gridData[row][col] !== chart.Id &&
              gridData[row][col] &&
              !chartsPosToUpdate.includes(gridData[row][col])
            ) {
              chartsPosToUpdate.push(gridData[row][col]);
            }
            grid[row][col] = chart.Id;
          }
        }
      }
    };

    checkForCollision();

    const voidPreviousFields = () => {
      let topLeftCorner;
      let bottomRightCorner;
      let previousSpaces = [];

      for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < 3; col++) {
          if (grid[row][col] === chart.Id) {
            if (
              row - 1 === -1 ||
              ((!grid[row][col - 1] || grid[row][col - 1] !== chart.Id) &&
                (!grid[row - 1][col] || grid[row - 1][col] !== chart.Id))
            ) {
              if (!topLeftCorner) {
                topLeftCorner = { row: row, col: col };
              }
            }
            if (
              !grid[row + 1] ||
              ((!grid[row][col + 1] || grid[row][col + 1] !== chart.Id) &&
                (!grid[row + 1][col] || grid[row + 1][col] !== chart.Id))
            ) {
              bottomRightCorner = { row: row, col: col };
            }
          }
          if (gridData[row][col] !== grid[row][col]) {
            previousSpaces.push({
              row: row,
              col: col,
            });
          }
        }
      }

      //Item moved top or left.
      const topOrLeft = _.find(
        previousSpaces,
        (space) =>
          space.row == topLeftCorner?.row && space.col == topLeftCorner?.col
      );
      //Item moved bottom or right.
      const bottomOrRight = _.find(
        previousSpaces,
        (space) =>
          space.row == bottomRightCorner?.row &&
          space.col == bottomRightCorner?.col
      );
      if (topOrLeft) {
        if (chart.Size === 3) {
          if (topOrLeft.row + 1 === bottomRightCorner.row) {
            // Item moved left
            grid[bottomRightCorner.row - 1][bottomRightCorner.col] = false;
            grid[bottomRightCorner.row][bottomRightCorner.col] = false;
          } else if (topOrLeft.col + 1 === bottomRightCorner.col) {
            //Item moved up
            grid[bottomRightCorner.row][bottomRightCorner.col - 1] = false;
            grid[bottomRightCorner.row][bottomRightCorner.col] = false;
            if (Math.abs(topLeftCorner.row - bottomRightCorner.row) === 3) {
              grid[bottomRightCorner.row - 1][bottomRightCorner.col - 1] =
                false;
              grid[bottomRightCorner.row - 1][bottomRightCorner.col] = false;
            }
          } else {
            //Item moved diagonaly top right
            if (topOrLeft.col === bottomRightCorner.col) {
              let count = 0;
              let maxCount = chart.Size == 3 ? 4 : chart.Size == 2 ? 2 : 1;
              for (let row = 0; row < gridData.length; row++) {
                for (let col = 0; col < 3; col++) {
                  if (
                    grid[row][col] == chart.Id &&
                    count !== maxCount &&
                    col !== 0
                  ) {
                    count += 1;
                  } else if (grid[row][col] == chart.Id) {
                    grid[row][col] = false;
                  }
                }
              }
            } else {
              let count = 0;
              let maxCount = chart.Size == 3 ? 4 : chart.Size == 2 ? 2 : 1;
              for (let row = 0; row < gridData.length; row++) {
                for (let col = 0; col < 3; col++) {
                  if (grid[row][col] == chart.Id && count !== maxCount) {
                    count += 1;
                  } else if (count === maxCount && grid[row][col] == chart.Id) {
                    grid[row][col] = false;
                  }
                }
              }
            }
          }
        } else if (chart.Size === 2) {
          if (topOrLeft.row === bottomRightCorner.row) {
            //Item moved left
            grid[bottomRightCorner.row][bottomRightCorner.col] = false;
          } else if (topOrLeft.col + 1 === bottomRightCorner.col) {
            //Item moved up
            grid[bottomRightCorner.row][bottomRightCorner.col - 1] = false;
            grid[bottomRightCorner.row][bottomRightCorner.col] = false;
          } else {
            //Item moved diagonaly top left
            let count = 0;
            let maxCount = chart.Size == 3 ? 4 : chart.Size == 2 ? 2 : 1;
            for (let row = 0; row < gridData.length; row++) {
              for (let col = 0; col < 3; col++) {
                if (grid[row][col] == chart.Id && count !== maxCount) {
                  count += 1;
                } else if (count === maxCount && grid[row][col] == chart.Id) {
                  grid[row][col] = false;
                }
              }
            }
          }
        } else if (chart.Size === 1) {
          if (topOrLeft.row === bottomRightCorner.row) {
            //Item moved left
            grid[bottomRightCorner.row][bottomRightCorner.col] = false;
          } else if (topOrLeft.col === bottomRightCorner.col) {
            //Item moved up
            grid[bottomRightCorner.row][bottomRightCorner.col] = false;
          } else {
            //Item moved diagonaly top left
            let count = 0;
            for (let row = 0; row < gridData.length; row++) {
              for (let col = 0; col < 3; col++) {
                if (grid[row][col] == chart.Id && count !== 1) {
                  count += 1;
                } else if (count === 1 && grid[row][col] == chart.Id) {
                  grid[row][col] = false;
                }
              }
            }
          }
        }
      }
      if (bottomOrRight) {
        if (chart.Size === 3) {
          if (bottomOrRight.row - 1 === topLeftCorner.row) {
            // item moved right
            grid[topLeftCorner.row + 1][topLeftCorner.col] = false;
            grid[topLeftCorner.row][topLeftCorner.col] = false;
          } else if (bottomOrRight.col - 1 === topLeftCorner.col) {
            // item moved down.{}
            grid[topLeftCorner.row][topLeftCorner.col] = false;
            grid[topLeftCorner.row][topLeftCorner.col + 1] = false;
            if (Math.abs(topLeftCorner.row - bottomRightCorner.row) === 3) {
              grid[topLeftCorner.row + 1][topLeftCorner.col] = false;
              grid[topLeftCorner.row + 1][topLeftCorner.col + 1] = false;
            }
          } else {
            //item moved diagonally bottom right
            let count = 0;
            let maxCount = chart.Size == 3 ? 4 : chart.Size == 2 ? 2 : 1;
            for (let row = gridData.length - 1; row >= 0; row--) {
              for (let col = 2; col >= 0; col--) {
                if (grid[row][col] == chart.Id && count !== maxCount) {
                  count += 1;
                } else if (count === maxCount && grid[row][col] == chart.Id) {
                  grid[row][col] = false;
                }
              }
            }
          }
        } else if (chart.Size === 2) {
          if (bottomOrRight.row === topLeftCorner.row) {
            // item moved right
            grid[topLeftCorner.row][topLeftCorner.col] = false;
          } else if (bottomOrRight.col - 1 === topLeftCorner.col) {
            // item moved down.
            grid[topLeftCorner.row][topLeftCorner.col + 1] = false;
            grid[topLeftCorner.row][topLeftCorner.col] = false;
          } else {
            //item moved diagonally bottom right
            let count = 0;
            let maxCount = chart.Size == 3 ? 4 : chart.Size == 2 ? 2 : 1;
            for (let row = gridData.length - 1; row >= 0; row--) {
              for (let col = 2; col >= 0; col--) {
                if (grid[row][col] == chart.Id && count !== maxCount) {
                  count += 1;
                } else if (count === maxCount && grid[row][col] == chart.Id) {
                  grid[row][col] = false;
                }
              }
            }
          }
        } else if (chart.Size === 1) {
          if (bottomOrRight.row === topLeftCorner.row) {
            // item moved right
            grid[topLeftCorner.row][topLeftCorner.col] = false;
          } else if (bottomOrRight.col === topLeftCorner.col) {
            // item moved down.
            grid[topLeftCorner.row][topLeftCorner.col] = false;
          } else {
            //item moved diagonally bottom right
            let count = 0;
            for (let row = gridData.length - 1; row >= 0; row--) {
              for (let col = 2; col >= 0; col--) {
                if (grid[row][col] == chart.Id && count !== 1) {
                  count += 1;
                } else if (count === 1 && grid[row][col] == chart.Id) {
                  grid[row][col] = false;
                }
              }
            }
          }
        }
      }
    };
    voidPreviousFields();
    let newGridCount = 0;
    if (chartsPosToUpdate.length > 0 || isEnd) {
      for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < 3; col++) {
          if (
            grid[row][col] !== chart.Id &&
            chartsPosToUpdate.includes(grid[row][col]) &&
            !isEnd
          ) {
            grid[row][col] = false;
          }
          if (grid[row][col]) {
            newGridCount += 1;
          }
        }
      }
    }
    const mapUngriddedCharts = () => {
      return _.reduce(
        chartsPosToUpdate,
        (chartsToUpdate, chartId) => {
          const updateChart = _.find(charts, (x) => x.Id === chartId);
          // check to make sure 2x2s or 1x2s don't get gridded again.
          //dragged chart is not added && existing chart isnt readded

          if (
            updateChart &&
            chartId !== chart.Id &&
            !_.find(chartsToUpdate, (y) => y.Id === chartId)
          ) {
            chartsToUpdate.push(updateChart);
          }
          return chartsToUpdate;
        },
        []
      );
    };
    const isTwoByTwo = () => {
      let size3Present = false;
      _.forEach(chartsPosToUpdate, (x) => {
        if (x) {
          if (_.find(charts, (y) => y.Id === x).Size == 3) {
            size3Present = true;
          }
        }
      });
      if (size3Present) {
        for (let row = 0; row < grid.length; row++) {
          for (let col = 0; col < 2; col++) {
            if (grid[row + 1] === null || grid[row + 1] === undefined) {
              break;
            }
            const subArray = [
              grid[row][col],
              grid[row][col + 1],
              grid[row + 1][col],
              grid[row + 1][col + 1],
            ];
            const falseCount = subArray.filter(
              (value) => value === false
            ).length;
            if (falseCount === 4) {
              return true;
            }
          }
        }
      } else return true;
    };
    if (
      mapUngriddedCharts().length > 0 &&
      ((chart.Size == 3 && isTwoByTwo()) || chart.Size != 3)
    ) {
      setGridData(grid);
      //Prioritize regridding charts with higher sizeTypes
      setUngriddedCharts(_.orderBy(mapUngriddedCharts(), "Size", "desc"));
    } else if (isEnd) {
      let oldGridCount = 0;
      for (let row = 0; row < gridData.length; row++) {
        for (let col = 0; col < 3; col++) {
          if (gridData[row][col]) {
            oldGridCount += 1;
          }
        }
      }
      //bandage 1 2 2
      //        2 3 3
      //        1 3 3
      if (oldGridCount === newGridCount) updateChartPositions(grid);
    }
  };

  const saveLayout = async () => {
    setLoading(true);
    const reorderedCharts = orderCharts(gridData, charts);
    let chartNullFields = _.map(reorderedCharts, (x) => {
      return {
        ...x,
        Data: undefined,
        editingMode: undefined,
        failToLoad: undefined,
      };
    });
    if (analyticConfig) {
      const defaultLayoutConfig = { ...analyticConfig.defaultGroup };
      defaultLayoutConfig[analyticConfig.viewingMode] =
        JSON.stringify(chartNullFields);
      analyticMgr.saveDefaultConfigByGroup(
        analyticConfig.defaultGroupType,
        defaultLayoutConfig
      );
    } else {
      await analyticMgr.saveChartLayout(chartNullFields);
      // setUpdatedAnalyticsConfig(JSON.stringify(chartNullFields))
    }

    setCharts(reorderedCharts);
    setLoading(false);
    setEditingLayout(false);
  };

  const handleDeleteChart = async (data) => {
    const updatedCharts = [...charts];
    const chartIndex = _.findIndex(charts, (x) => x.Id === data.Id);
    if (chartIndex !== -1) {
      updatedCharts.splice(chartIndex, 1);
    }

    const grid = convertChartsToGrid(updatedCharts, true);
    setGridData(grid);
    setChartToDelete(null);
    setCharts(updateChartPositions(grid));
  };

  const handleEditChart = (data) => {
    setChartConfig({ ...data, editingMode: true });
    setPreviewChartData({ ...data, editingMode: true });
    setPreviewChartKeys(_.sortBy(data.keys, (x) => x.text));
    setOpenAddChartDialog(true);
    if (data.ClientIds && data.ClientIds.length > 0) {
      updateDistricts(data.ClientIds);
    }
    if (data.DistrictIds && data.DistrictIds.length > 0) {
      updateSchools(data.DistrictIds);
    }
    if (data.SchoolIds && data.SchoolIds.length > 0) {
      updateClusters(data.schoolIds);
    }
  };

  const updateDistricts = async (organizations) => {
    let districtList = [];
    for (let i = 0; i < organizations.length; i++) {
      var r = await analyticMgr.grabGroup("District", organizations[i]);
      districtList = [...districtList, ...r];
    }

    setDistricts(districtList);
  };

  const updateSchools = async (districts) => {
    let schoolList = [];
    for (let i = 0; i < districts.length; i++) {
      var r = await analyticMgr.grabGroup("School", districts[i]);
      schoolList = [...schoolList, ...r];
    }
    setSchools(schoolList);
  };
  const updateClusters = async (schools) => {
    analyticMgr.grabGroup("Cluster", schools).then((r) => setClusters(r));
  };

  return (
    <>
      <ChartConfigurationDialog
        openAddChartDialog={openAddChartDialog}
        setOpenAddChartDialog={setOpenAddChartDialog}
        setChartConfig={setChartConfig}
        analyticConfig={analyticConfig}
        analyticMgr={analyticMgr}
        chartConfig={chartConfig}
        setPreviewChartData={setPreviewChartData}
        setPreviewChartKeys={setPreviewChartKeys}
        setClusters={setClusters}
        previewChartData={previewChartData}
        previewChartKeys={previewChartKeys}
        orgs={orgs}
        updateDistricts={updateDistricts}
        districts={districts}
        updateSchools={updateSchools}
        schools={schools}
        clusters={clusters}
        schoolYears={schoolYears}
        charts={charts}
        gridData={gridData}
        convertChartsToGrid={convertChartsToGrid}
        setGridData={setGridData}
        setCharts={setCharts}
        updateChartPositions={updateChartPositions}
        setOriginalCharts={setOriginalCharts}
        setOriginalGridData={setOriginalGridData}
      />
      <DialogControl
        title={`Are you sure you want to delete ${chartToDelete?.Name}?`}
        disableXButton
        openDialog={chartToDelete}
        loadingOk={loading}
        onCancel={() => {
          setChartToDelete(false);
        }}
        onOk={() => {
          handleDeleteChart(chartToDelete);
        }}
      />

      <MainLayout className={"analytics"} errors={errors}>
        <ScreenWrapper loading={loading}>
          <>
            <div className="analytics-header-wrapper">
              <h3 className="categoryTitle">Analytics</h3>
              {!editingLayout ? (
                <div className="button-wrapper">
                  <ButtonControl
                    type={"okay"}
                    onClick={() => {
                      setEditingLayout(true);
                      setOriginalCharts(charts);
                      setOriginalGridData(_.cloneDeep(gridData));
                      setOriginalStyles(styles);
                    }}
                  >
                    Edit Layout
                  </ButtonControl>
                </div>
              ) : (
                <div className="button-wrapper">
                  <ButtonControl
                    type={"cancel"}
                    onClick={() => {
                      setEditingLayout(false);
                      setCharts(originalCharts);
                      setGridData(originalGridData);
                      setStyles(originalStyles);
                    }}
                  >
                    Cancel
                  </ButtonControl>
                  <ButtonControl type={"cancel"} onClick={() => saveLayout()}>
                    {!analyticConfig ? "Save Layout" : "Save Default Layout"}
                  </ButtonControl>
                  <ButtonControl
                    type={"create"}
                    onClick={() => setOpenAddChartDialog(true)}
                  >
                    Add Chart
                  </ButtonControl>
                </div>
              )}
            </div>
            {charts && charts.length > 0 ? (
              <div
                className="analytics-grid-container"
                style={{ height: `${screenHeight}px` }}
              >
                {charts?.map((chart, index) => {
                  return (
                    <AnalyticsChart
                      key={index}
                      editingLayout={editingLayout}
                      id={chart.Id}
                      data={chart}
                      size={chart.Size}
                      type={chart.chartType}
                      failToLoad={chart.failToLoad}
                      styles={styles}
                      updateGrid={updateGrid}
                      handleEdit={handleEditChart}
                      handleDelete={setChartToDelete}
                      setErrors={setErrors}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="analytic-instructions">
                You currently do not have any charts configured. Select "Edit
                Layout" and then "Add Chart" to begin adding charts to your
                Analytics Dashboard.
              </div>
            )}
          </>
        </ScreenWrapper>
      </MainLayout>
    </>
  );
};

export default Analytics;
